import React, { useState, useRef } from "react";

/**
 * compute viewport position x,y from longitude and latitude
 * @returns viewport position { x, y } in pixel
 */
const getViewportPositionFromLonLat = (
  vp: any,
  mapping: any,
  lon: any,
  lat: any,
): { left: any; top: any } => {
  let lonLatWidth: any, lonLatHeight: any;

  if (mapping.mapLowerRight.lng < mapping.mapUpperLeft.lng) {
    lonLatWidth = mapping.mapLowerRight.lng + (360 - mapping.mapUpperLeft.lng);
  } else {
    lonLatWidth = mapping.mapLowerRight.lng - mapping.mapUpperLeft.lng;
  }
  lonLatHeight = mapping.mapUpperLeft.lat - mapping.mapLowerRight.lat;

  let lngDiff, latDiff;
  if (lon < mapping.mapUpperLeft.lng) {
    lngDiff = lon + (360 - mapping.mapUpperLeft.lng);
  } else {
    lngDiff = lon - mapping.mapUpperLeft.lng;
  }
  latDiff = mapping.mapUpperLeft.lat - lat;

  const x: number = (vp.width * lngDiff) / lonLatWidth;
  const y: number = (vp.height * latDiff) / lonLatHeight;

  return {
    left: vp.left + x,
    top: vp.top + y,
  };
};

/**
 * compute longitude and latitude from viewport position
 * @returns geo coordinates of viewport position { lat, lng }
 */
const getLonLatFromViewportPosition = (
  vp: any,
  mapping: any,
  x: any,
  y: any,
): { lat: number; lng: any } => {
  let lonLatWidth: any, lonLatHeight: any;

  if (mapping.mapLowerRight.lng < mapping.mapUpperLeft.lng) {
    lonLatWidth = mapping.mapLowerRight.lng + (360 - mapping.mapUpperLeft.lng);
  } else {
    lonLatWidth = mapping.mapLowerRight.lng - mapping.mapUpperLeft.lng;
  }
  lonLatHeight = mapping.mapUpperLeft.lat - mapping.mapLowerRight.lat;

  const latDiff: number = (y / vp.height) * lonLatHeight;
  const lngDiff: number = (x / vp.width) * lonLatWidth;
  let lat: number = mapping.mapUpperLeft.lat - latDiff;
  let lng: any = mapping.mapUpperLeft.lng + lngDiff;
  if (lng >= 360) lng = lng - 360;

  return {
    lat: lat,
    lng: lng,
  };
};

const MapOverlay = (props: any) => {
  const [mapInitialized, setMapInitialized] = useState(false); // map instance has been set
  const [viewportInitialized, setViewportInitialized] = useState(false); // viewport has been fully defined
  const [viewport, setViewport] = useState({
    // viewport prototype with valid default values
    left: 0,
    top: 0,
    width: 0,
    height: 0,
    mapUpperLeft: {},
    mapLowerRight: {},
  });
  const viewportRef = useRef(viewport);
  const [map, setMap] = useState(props.map);
  const mapRef = useRef(map);
  const [canvasContext, setCanvasContext] = useState(null);
  const canvasContextRef = useRef(canvasContext);
  const [styleInitialized, setStyleInitialized] = useState(false); // canvas style has been set
  const [canvasStyle, setCanvasStyle] = useState({
    // canvas style behind map if not set, overwritten by props
    zIndex: 0,
  });
  const [id, setId] = useState("overlay-canvas-"); // prefix of "unique" canvas id

  const _setViewport = (vp: any) => {
    viewportRef.current = vp;
    setViewport(vp);
  };

  const _setCanvasContext = (ctx: any) => {
    canvasContextRef.current = ctx;
    setCanvasContext(ctx);
  };

  let viewportMapping = {
    mapUpperLeft: {
      lng: 0,
      lat: 0,
    },
    mapLowerRight: {
      lng: 1,
      lat: 1,
    },
  };

  /**
   * update lonlat borders of viewport to achieve mapping between viewport position and map position
   */
  const getViewportMapping = (map: any) => {
    if (typeof map.getBounds() === "undefined" || map.getBounds() === null) {
      console.log("map does not return bounds");
      return viewportMapping;
    }
    const mapUpperLeft = {
      lat: map.getBounds().getNorthEast().lat(),
      lng: map.getBounds().getSouthWest().lng(),
    };
    const mapLowerRight = {
      lng: map.getBounds().getNorthEast().lng(),
      lat: map.getBounds().getSouthWest().lat(),
    };
    return {
      mapUpperLeft: mapUpperLeft,
      mapLowerRight: mapLowerRight,
    };
  };

  var UIState = {
    started: {
      overlayInteraction: false,
      mapInteraction: false,
      timeStamp: 0,
      offsetX: 0,
      offsetY: 0,
    },
    dragStatus: {
      inProgress: false,
      lastMoved: 0,
    },
  };

  const mouseDownHandler = (e: any): void => {
    console.log("MDOWN");

    if (typeof props.contentTouched === "function") {
      if (props.contentTouched(e.offsetX, e.offsetY) === false) {
        console.log("start map interaction");
        UIState.started.mapInteraction = true; // map interaction has been started
        UIState.started.timeStamp = e.timeStamp; // time stamp of begin
        UIState.started.offsetX = e.offsetX;
        UIState.started.offsetY = e.offsetY;
      } else {
        console.log("start overlay interaction");
        UIState.started.overlayInteraction = true;
        UIState.started.timeStamp = e.timeStamp;
      }
    } else {
      console.log("start map interaction");
      UIState.started.mapInteraction = true; // map interaction has been started
      UIState.started.timeStamp = e.timeStamp; // time stamp of begin
      UIState.started.offsetX = e.offsetX;
      UIState.started.offsetY = e.offsetY;
    }
  };

  const mouseMoveHandler = (e: any): void => {
    //console.log("MMOVE");

    if (UIState.started.mapInteraction || UIState.started.overlayInteraction) {
      UIState.dragStatus.inProgress = true;
      UIState.dragStatus.lastMoved = e.timeStamp;

      //console.log("MMOVE: ",e);

      if (UIState.started.mapInteraction) {
        //console.log("move map center, update viewport corners");
        var newCenter = getLonLatFromViewportPosition(
          viewportRef.current,
          viewportMapping,
          viewportRef.current.width / 2.0 - e.movementX,
          viewportRef.current.height / 2.0 - e.movementY,
        );
        console.log("new center: ", props.api.maps.LatLng(newCenter.lat, newCenter.lng));
        mapRef.current.setCenter(props.api.maps.LatLng(newCenter.lat, newCenter.lng));
      } else {
        console.log("call overlay drag if set");
        // not to be implemented yet
      }
    }
  };

  const mouseUpHandler = (e: any): void => {
    console.log("MUP: ", e);
  };

  const dblClickHandler = (e: any): void => {
    console.log("DBLCLICK: ", e);
    mapRef.current.setZoom(mapRef.current.getZoom() + 1);
  };

  const clickHandler = (e: any): void => {
    console.log("MCLICK: ", e);
    //if (eventStatus.lastEvent.type === 'mouseup' && e.timeStamp - eventStatus.lastEvent.timeStamp < 100) return;
    if (UIState.dragStatus.inProgress) {
      UIState.dragStatus.inProgress = false;
      UIState.started.mapInteraction = false;
      UIState.started.overlayInteraction = false;
    } else {
      console.log("... CLICK: ", e);
      UIState.started.mapInteraction = false;
      UIState.started.overlayInteraction = false;

      // need google maps api reference to generate latlng and trigger click events
      const mapPos = getLonLatFromViewportPosition(
        viewportRef.current,
        viewportMapping,
        UIState.started.offsetX,
        UIState.started.offsetY,
      );
      var mapEvent = {
        latLng: new props.api.maps.LatLng(mapPos.lat, mapPos.lng),
      };
      console.log("map event: ", mapEvent);
      props.api.maps.event.trigger(mapRef.current, "click", mapEvent);
    }
  };

  /**
   * set up local map reference and event listener of google map if not set yet,
   * set up viewport mapping according to map boundings,
   * set up viewport dimensions according to DOM map container
   * set up canvas style if set by props
   * @returns
   */
  const initialize = () => {
    if (viewportInitialized) return;

    if (!mapInitialized && typeof props.map !== "undefined" && props.map !== null) {
      const mapContainer: HTMLElement | null = document.getElementById(props.mapContainerId);
      let vp = { ...viewport };
      if (typeof mapContainer !== "undefined") {
        const bounds: DOMRect | undefined = mapContainer?.getBoundingClientRect();
        if (typeof bounds !== "undefined") {
          vp.left = bounds.left;
          vp.top = bounds.top;
          vp.width = bounds.width;
          vp.height = bounds.height;
        }
      }
      setMapInitialized(true);
      _setViewport(vp);
      setId(id + document.getElementsByClassName("inb-react-map-overlay-canvas").length);
    }
    if (
      mapInitialized &&
      typeof props.map !== "undefined" &&
      props.map !== null &&
      typeof props.map.getBounds() !== "undefined"
    ) {
      console.log("init viewport");
      let vp: {
        left: number;
        top: number;
        width: number;
        height: number;
        mapUpperLeft: {};
        mapLowerRight: {};
      } = { ...viewport };
      //let mapping = getViewportMapping(props.map);
      _setViewport(vp);
      // @ts-ignore
      _setCanvasContext(document.getElementById(id).getContext("2d"));
      setViewportInitialized(true);

      props.map.addListener("bounds_changed", () => {
        let vp = { ...viewportRef.current };
        viewportMapping = getViewportMapping(mapRef.current);
        //_setViewport(vp);
        if (typeof props.render === "function")
          props.render(canvasContextRef.current, vp, viewportMapping);
      });

      props.map.addListener("zoom_changed", () => {
        let vp = { ...viewportRef.current };
        //let mapping = getViewportMapping(props.map);
        _setViewport(vp);
      });

      // add event listener to be capable of moving, zooming and clicking the map

      /**
       * mousedown und touchdown: call parent to get feedback if an overlay object has been clicked
       *
       * if no object hit: start map interaction, otherwise do nothing (parent must do the job)
       */

      const canvasElement: HTMLElement | null = document.getElementById(id);
      if (canvasElement) {
        canvasElement.addEventListener("click", (e) => {
          clickHandler(e);
        });
        canvasElement.addEventListener("dblclick", (e) => {
          dblClickHandler(e);
        });
        canvasElement.addEventListener("drag", (e) => {
          console.log("DRAG: ", e);
        });
        canvasElement.addEventListener("mousemove", (e) => {
          mouseMoveHandler(e);
        });
        canvasElement.addEventListener("touchmove", (e) => {
          console.log("TMOVE: ", e);
        });
        canvasElement.addEventListener("mousedown", (e) => {
          mouseDownHandler(e);
        });
        canvasElement.addEventListener("mouseup", (e) => {
          mouseUpHandler(e);
        });
      }
    }
    if (!styleInitialized && mapInitialized) {
      let _canvasStyle: {
        zIndex: number;
        position?: string;
        left?: string;
        top?: string;
      } = { ...canvasStyle };
      _canvasStyle.position = "fixed";
      _canvasStyle.zIndex = 8; // set canvas over map
      _canvasStyle.left = viewportRef.current.left + "px";
      _canvasStyle.top = viewportRef.current.top + "px";
      if (typeof props.style !== "undefined" && props.style !== null) {
        Object.assign(canvasStyle, props.style);
      }
      setStyleInitialized(true);
      setCanvasStyle(_canvasStyle);
    }
  };

  initialize();
  mapRef.current = props.map; // set up for event listeners without re-render of component
  console.log("RENDER OVERLAY - update map ref");

  return (
    <canvas
      id={id}
      width={viewport.width}
      height={viewport.height}
      className="inb-react-map-overlay-canvas"
      style={canvasStyle}
    ></canvas>
  );
};

export default MapOverlay;
export { getViewportPositionFromLonLat, getLonLatFromViewportPosition };
